import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { take } from 'rxjs';
import { TcsPermission } from 'src/app/shared/authorization/permissions';
import { UserViewModel } from 'src/models/person-view-model';
import { firstValueOrUndefined } from 'src/util/rxjs';
import { PermissionsService } from '../amg-authorization/permissions.service';
import { UserService } from '../tcs-identity/user.service';
import { OpsAuthorizationService } from './ops-authorization.service';
import { OrgAuthorizationService } from './org-authorization.service';
import { InsightsAuthorizationService } from './insights-authorization.service';
import { OosAuthorizationService } from './oos-authorization.service';
import {
  AsbAuthorizationStrategy,
  collectAuthorizations,
  FleetStatusAuthorizationStrategy,
  getAuthorizationStrategies,
  InsightsAuthorizationStrategy,
  OosRumAuthorizationStrategy,
  OpsAuthorizationStrategy,
  OrgAuthorizationStrategy,
  RouteAuthorizationStatus,
  RouteAuthorizationStrategy,
  RttAuthorizationStrategy,
} from './route-authorization-strategies';

@Injectable({ providedIn: 'root' })
export class RouteAuthorizationService {
  private _routeStrategies = new Map<string, RouteAuthorizationStrategy>();
  constructor(
    private _authUser: UserService,
    private _permissions: PermissionsService,
    private _opsAuthz: OpsAuthorizationService,
    private _orgAuthz: OrgAuthorizationService,
    private _oosAuthz: OosAuthorizationService,
    private _insightsAuthz: InsightsAuthorizationService,
  ) {
    this._registerStrategies([
      new OpsAuthorizationStrategy(this._opsAuthz),
      new OrgAuthorizationStrategy(this._orgAuthz),
      new InsightsAuthorizationStrategy(this._insightsAuthz),
      new OosRumAuthorizationStrategy(this._oosAuthz),
      new AsbAuthorizationStrategy(this._permissions),
      new FleetStatusAuthorizationStrategy(this._permissions),
      new RttAuthorizationStrategy(this._permissions),
    ]);
  }

  async applyGuard(route: ActivatedRouteSnapshot): Promise<RouteAuthorizationStatus> {
    const user = await firstValueOrUndefined(this._authUser.user$.pipe(take(1)));

    if (user == null || user.grantTcsAccess !== true) {
      return RouteAuthorizationStatus.NoAccessGranted;
    }

    const roles = user.userRolesAllScopes ?? [];
    const permissions = collectAuthorizations(route);

    if (roles.length === 0 && user.allCommunicationCentersAccessible !== true) {
      return RouteAuthorizationStatus.NoRolesGranted;
    }

    const authStatus = await this._applyStrategies(route, user, permissions);
    if (authStatus != null) {
      return authStatus;
    }

    if (this._permissions.hasAnyPermission(roles, permissions)) {
      return RouteAuthorizationStatus.Authorized;
    }

    return RouteAuthorizationStatus.Unauthorized;
  }

  private async _applyStrategies(
    route: ActivatedRouteSnapshot,
    user: UserViewModel,
    permissions: TcsPermission[],
  ): Promise<RouteAuthorizationStatus | null> {
    const strategies = getAuthorizationStrategies(route);
    const routeStrategies = strategies
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .map((strategy) => this._routeStrategies.get(strategy)!)
      .filter((s) => s != null);
    if (routeStrategies.length === 0) {
      return null;
    }
    const authResults = await Promise.all(
      routeStrategies.map((strategy) => strategy.isAuthorized(route, user, permissions)),
    );
    return authResults.every((isAuthorized) => isAuthorized)
      ? RouteAuthorizationStatus.Authorized
      : RouteAuthorizationStatus.Unauthorized;
  }

  private _registerStrategies(strategies: RouteAuthorizationStrategy[]) {
    strategies.forEach((strategy) => this._registerStrategy(strategy));
  }

  private _registerStrategy(strategy: RouteAuthorizationStrategy) {
    if (!this._routeStrategies.has(strategy.name)) {
      this._routeStrategies.set(strategy.name, strategy);
    }
  }
}
